import Vue from 'vue'
import { db, timestamp } from '../firebase/core'
import deepcopy from 'deepcopy/dist/deepcopy.min.js'

function swap(a,x,y) {
  a[x]=[a[y], a[y]=a[x]][0]
  return a;
}

function getSortedKeys(items, sortKey) {
  return Object.keys(items).sort((a, b) => {
    const aData = items[a]
    const bData = items[b]
    if (!(sortKey in aData) && !(sortKey in bData)) {
      return 0
    }
    if ( !(sortKey in aData) ) {
      return -1
    }
    if ( !(sortKey in bData) ) {
      return 1
    }
    const aVal = aData[sortKey].toMillis ? aData[sortKey].toMillis() : aData[sortKey]
    const bVal = bData[sortKey].toMillis ? bData[sortKey].toMillis() : bData[sortKey]
    if (aVal === bVal && aData.timestamp &&  bData.timestamp) {
      const aTime = aData.timestamp.toMillis ? aData.timestamp.toMillis() : 0
      const bTime = bData.timestamp.toMillis ? bData.timestamp.toMillis() : 0
      return aTime - bTime
    }
    return aVal - bVal
  })
}

const baseMutations = {
  _add(state, { key, data }) {
    Vue.set(state.items, key, state.$formatter(data))
    // state.items[key] = data
    // state.indexies = getSortedKeys(state.items)
    if (state.indexies.indexOf(key) < 0) {
      state.indexies.push(key)
    }
  },
  _set(state, { key, data }) {
    // if (!data.timestamp) return // blocked timestamp === null
    Object.assign(state.items[key], state.$formatter(data))
    // state.indexies = getSortedKeys(state.items)
  },
  _remove(state, { key }) {
    Vue.delete(state.items, key)
    // delete state.items[key]
    // state.indexies = getSortedKeys(state.items)
    if (state.indexies.indexOf(key) > -1) {
      state.indexies.splice(state.indexies.indexOf(key), 1)
    }
  },
  $checkout(state, key) {
    state.currentKey = key
    state.currentData = state.$clone(state.items[key])
  },
  $change(state, { key, value }) {
    if (!state.currentData || state.currentData[key] === undefined) return
    state.currentData[key] = value
  }
}

const baseActions = {
  async $create({ state, commit }, data) {
    const nextData = Object.assign({}, state.$formatter(data), { timestamp: state.$timestamp() })
    const doc = await state.$collection.add(nextData)
    commit('$checkout', doc.id)
  },
  $insert({ state }, { key, data }) {
    const nextData = Object.assign({}, state.$formatter(data), { timestamp: state.$timestamp() })
    return state.$collection.doc(key).set(nextData)
  },
  $filter({ state, dispatch }, keys) {
    return Promise.all(Object.keys(state.items).map(key => {
      if (keys.indexOf(key) > -1) return Promise.resolve()
      return dispatch('$remove', key)
    }))
  },
  $save({ state }) {
    if (!state.currentData) return
    const nextData = Object.assign({}, state.$formatter(state.currentData))
    return state.$collection.doc(state.currentKey).set(nextData, { merge: true })
  },
  $index({ state, dispatch }, { key, index }) {
    const keys = getSortedKeys(state.items, state.$sortKey)
    const currentIndex = keys.indexOf(key)
    if (currentIndex > -1 && index < keys.length) {
      const swappedKeys = swap(keys, currentIndex, index)
      swappedKeys.forEach((k, i) => {
        if (state.items[k] && state.items[k].order !== i) {
          return dispatch('$update', { key: k, data: { order: i } })
        }
      })
    }
  },
  $duplicate({ state }) {
    if (!state.currentData || !state.currentKey) return
    const item = state.items[state.currentKey]
    const timestamp = state.$timestamp.fromMillis(item.timestamp.toMillis() + 1000)
    const nextData = Object.assign({}, state.$formatter(state.currentData), { timestamp })
    return state.$collection.add(nextData)
  },
  $update({ state }, { key, data }) {
    const item = state.items[key]
    if (!item) return Promise.reject()
    const nextData = state.$formatter(Object.assign({}, item, data))
    // nextData.timestamp = state.$timestamp()
    return state.$collection.doc(key).set(nextData, { merge: true })
  },
  $remove({ state, commit }, key) {
    state.$collection.doc(key).delete()
    commit('$checkout', null)
  },
  $removeAll({ state, commit }) {
    state.indexies.forEach((key) => {
      state.$collection.doc(key).delete()
    })
    commit('$checkout', null)
  },
  $connect({ state, commit }, ref) {
    if (state.$connection) return
    state.$collection = db.doc(ref).collection(state.$name)
    state.$connection = state.$collection.orderBy('timestamp').onSnapshot(snapshot => {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'removed') {
          commit('_remove', { key: change.doc.id })
        }
        if (change.type === 'modified') {
          commit('_set', { key: change.doc.id, data: change.doc.data() })
        }
        if (change.type === 'added') {
          commit('_add', { key: change.doc.id, data: change.doc.data() })
        }
      })
    })
  },
  $disconnect({ state }) {
    if (!state.$connection) return
    state.$connection()
  }
}

const baseGetters = {
  $get: state => key => {
    return state.items[key]
  },
  $currentKey(state) {
    return state.currentKey
  },
  $currentData(state) {
    return {
      key: state.currentKey,
      ...state.currentData
    }
  },
  $items(state) {
    return state.indexies.map(key => {
      return {
        key,
        ...state.items[key]
      }
    })
  },
  $count(state) {
    return state.indexies.length
  },
  $sorted(state) {
    return getSortedKeys(state.items, state.$sortKey).map(key => {
      return {
        key,
        ...state.items[key]
      }
    })
  }
}

export default function({ $name, $formatter, state, mutations, getters, actions, $sortKey = 'timestamp' }) {
  return {
    namespaced: true,
    state: Object.assign({
      items: {},
      indexies: [],
      currentKey: null,
      currentData: null,
      $name,
      $formatter,
      $collection: null,
      $connection: null,
      $timestamp: timestamp,
      $clone: deepcopy,
      $sortKey
    }, state),
    mutations: Object.assign({}, baseMutations, mutations),
    getters: Object.assign({}, baseGetters, getters),
    actions: Object.assign({}, baseActions, actions)
  }
}
